import React, { useState } from 'react';
import { Camera, Calendar, MapPin, Play, X, ChevronLeft, ChevronRight, Rocket, Handshake, Briefcase, TrendingUp, Star, PlayCircle, Award, Building2, ExternalLink } from 'lucide-react';
import { memoriesData } from '../data/mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const iconMap = { rocket: Rocket, handshake: Handshake, briefcase: Briefcase, 'trending-up': TrendingUp, star: Star, play: PlayCircle, building: Building2 };

const Memories = () => {
  const [activeTab, setActiveTab] = useState('gallery');
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryFilter, setGalleryFilter] = useState('All');
  const [playingVideo, setPlayingVideo] = useState(null);

  const tabs = [
    { id: 'gallery', label: 'Photo Gallery', icon: Camera },
    { id: 'timeline', label: 'Milestones', icon: Calendar },
    { id: 'events', label: 'Events', icon: Award },
    { id: 'stories', label: 'Stories', icon: Play }
  ];

  const galleryCategories = ['All', ...new Set(memoriesData.photoGallery.map(p => p.category))];
  const filteredPhotos = galleryFilter === 'All' ? memoriesData.photoGallery : memoriesData.photoGallery.filter(p => p.category === galleryFilter);

  const openLightbox = (item) => { if (item.type === 'video') { setPlayingVideo(item); } else { setSelectedImage(item); } };
  const closeLightbox = () => { setSelectedImage(null); setPlayingVideo(null); };
  const navigateImage = (direction) => {
    const currentIndex = filteredPhotos.findIndex(p => p.id === selectedImage.id);
    let newIndex;
    if (direction === 'next') { newIndex = currentIndex === filteredPhotos.length - 1 ? 0 : currentIndex + 1; }
    else { newIndex = currentIndex === 0 ? filteredPhotos.length - 1 : currentIndex - 1; }
    setSelectedImage(filteredPhotos[newIndex]);
  };

  return (
    <div className="min-h-screen bg-[#faf6ee]" data-testid="wall-of-fame-page">
      <Header />

      <section className="pt-32 pb-12 bg-[#faf6ee]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-4">Achievements & Recognition</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#152846] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Wall of <span className="text-gold-400">Fame</span></h1>
            <p className="text-lg text-[#5c6878] leading-relaxed">A collection of achievements, milestones, and stories from over two decades of leadership, innovation, and building meaningful connections.</p>
          </div>
        </div>
      </section>

      <section className="sticky top-20 z-40 bg-[#f3ede0]/95 backdrop-blur-md border-b border-[#e6dfd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 py-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-gold-400 text-black shadow-lg shadow-gold-400/20' : 'bg-[#fffdf8] text-[#5c6878] border border-[#e6dfd0] hover:border-gold-400/30'}`}
              >
                <tab.icon size={18} strokeWidth={2.5} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="py-16">
        {activeTab === 'gallery' && (
          <section className="max-w-7xl mx-auto px-6 lg:px-8">
            {memoriesData.photoGallery.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-[#fffdf8] rounded-full flex items-center justify-center mx-auto mb-6"><Camera className="w-10 h-10 text-[#aab2bf]" strokeWidth={2.5} /></div>
                <h3 className="text-xl font-semibold text-[#3e4858] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Photo Gallery Coming Soon</h3>
                <p className="text-[#8b94a3] max-w-md mx-auto">Professional photos and memorable moments will be added here soon.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                  {galleryCategories.map((category) => (
                    <button key={category} onClick={() => setGalleryFilter(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${galleryFilter === category ? 'bg-gold-400 text-black' : 'bg-[#fffdf8] text-[#5c6878] border border-[#e6dfd0] hover:border-gold-400/30'}`}
                      data-testid={`gallery-filter-${category.toLowerCase()}`}
                    >{category}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredPhotos.map((photo, index) => (
                    <div key={photo.id} onClick={() => openLightbox(photo)}
                      className={`group relative overflow-hidden rounded-xl cursor-pointer border border-[#e6dfd0] hover:border-gold-400/30 transition-all ${index === 0 ? 'col-span-2 row-span-2' : ''}`}
                      data-testid={`gallery-item-${photo.id}`}
                    >
                      {photo.type === 'video' ? (
                        <>
                          <img src={photo.thumbnail} alt={photo.caption} className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${index === 0 ? 'h-full min-h-[400px]' : 'h-48'}`} />
                          <div className="absolute inset-0 flex items-center justify-center"><div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><PlayCircle className="w-8 h-8 text-[#152846]" strokeWidth={2.5} /></div></div>
                        </>
                      ) : (
                        <img src={photo.image} alt={photo.caption} className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${index === 0 ? 'h-full min-h-[400px]' : 'h-48'}`} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <span className="inline-block px-2 py-1 bg-gold-400 text-black text-xs rounded mb-2 font-semibold">{photo.type === 'video' ? 'Video' : photo.category}</span>
                        <p className="text-[#152846] text-sm font-medium line-clamp-2">{photo.caption}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}

        {activeTab === 'timeline' && (
          <section className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="relative">
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gold-400/20 transform md:-translate-x-1/2" />
              <div className="space-y-12">
                {memoriesData.milestones.map((milestone, index) => {
                  const IconComponent = iconMap[milestone.icon] || Star;
                  return (
                    <div key={milestone.id} className={`relative flex items-start gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-8 md:left-1/2 w-16 h-16 bg-gold-400 rounded-full flex items-center justify-center transform -translate-x-1/2 z-10 shadow-lg shadow-gold-400/20">
                        <IconComponent className="w-7 h-7 text-black" strokeWidth={2.5} />
                      </div>
                      <div className={`ml-24 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                        <div className="bg-[#fffdf8] p-6 rounded-2xl border border-[#e6dfd0] hover:border-gold-400/30 transition-all duration-300">
                          <span className="inline-block px-3 py-1 bg-gold-400/10 text-gold-400 text-sm font-bold rounded-full mb-3">{milestone.year}</span>
                          <h3 className="text-xl font-bold text-[#152846] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{milestone.title}</h3>
                          <p className="text-[#5c6878]">{milestone.description}</p>
                        </div>
                      </div>
                      <div className="hidden md:block md:w-1/2" />
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'events' && (
          <section className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {memoriesData.events.map((event) => {
                const CardTag = event.link ? 'a' : 'article';
                const linkProps = event.link
                  ? { href: event.link, target: '_blank', rel: 'noopener noreferrer' }
                  : {};
                return (
                <CardTag key={event.id} {...linkProps} className="group bg-[#fffdf8] rounded-2xl overflow-hidden border border-[#e6dfd0] hover:border-gold-400/30 hover:shadow-xl transition-all duration-300 block" data-testid={`event-card-${event.id}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <span className={`absolute top-4 left-4 px-3 py-1 text-black text-xs font-semibold rounded-full ${event.type === 'Conference' ? 'bg-blue-400' : event.type === 'Launch' ? 'bg-green-400' : event.type === 'Award' ? 'bg-gold-400' : event.type === 'Feature' ? 'bg-[#c77b5a] text-white' : event.type === 'Membership' ? 'bg-[#152846] text-white' : 'bg-purple-400'}`}>{event.type}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#8b94a3] mb-3">
                      <span className="flex items-center gap-1"><Calendar size={14} strokeWidth={2.5} />{event.date}</span>
                      <span className="flex items-center gap-1"><MapPin size={14} strokeWidth={2.5} />{event.location}</span>
                    </div>
                    <h3 className="text-xl font-bold text-[#152846] mb-3 group-hover:text-gold-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{event.title}</h3>
                    <p className="text-[#5c6878]">{event.description}</p>
                    {event.link && (
                      <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-gold-400">
                        Read article <ExternalLink size={14} strokeWidth={2.5} />
                      </span>
                    )}
                  </div>
                </CardTag>
                );
              })}
            </div>
          </section>
        )}

        {activeTab === 'stories' && (
          <section className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="space-y-12">
              {memoriesData.stories.map((story, index) => (
                <article key={story.id} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`} data-testid={`story-card-${story.id}`}>
                  <div className="w-full md:w-1/2">
                    <div className="relative rounded-2xl overflow-hidden shadow-lg border border-[#e6dfd0]">
                      <img src={story.image} alt={story.title} className="w-full h-auto object-contain" />
                    </div>
                  </div>
                  <div className="w-full md:w-1/2">
                    <span className="inline-block px-3 py-1 bg-gold-400/10 text-gold-400 text-sm font-semibold rounded-full mb-4">{story.date}</span>
                    <h3 className="text-2xl font-bold text-[#152846] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{story.title}</h3>
                    <p className="text-[#5c6878] text-lg leading-relaxed">{story.content}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button onClick={closeLightbox} className="absolute top-6 right-6 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-[#152846] hover:bg-white/20 transition-colors"><X size={24} strokeWidth={2.5} /></button>
          <button onClick={() => navigateImage('prev')} className="absolute left-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-[#152846] hover:bg-white/20 transition-colors"><ChevronLeft size={28} strokeWidth={2.5} /></button>
          <button onClick={() => navigateImage('next')} className="absolute right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-[#152846] hover:bg-white/20 transition-colors"><ChevronRight size={28} strokeWidth={2.5} /></button>
          <div className="max-w-4xl max-h-[80vh] flex flex-col items-center">
            <img src={selectedImage.image} alt={selectedImage.caption} className="max-h-[70vh] object-contain rounded-lg" />
            <div className="mt-4 text-center">
              <span className="inline-block px-3 py-1 bg-gold-400 text-black text-xs rounded-full mb-2 font-semibold">{selectedImage.category}</span>
              <p className="text-[#152846] text-lg">{selectedImage.caption}</p>
              {selectedImage.link && (
                <a
                  href={selectedImage.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-gold-400 text-black text-sm font-semibold rounded-full hover:bg-[#dbc073] transition-colors"
                  data-testid={`lightbox-link-${selectedImage.id}`}
                >
                  {selectedImage.linkLabel || 'Read article'}
                  <ExternalLink size={14} strokeWidth={2.5} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {playingVideo && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button onClick={closeLightbox} className="absolute top-6 right-6 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-[#152846] hover:bg-white/20 transition-colors z-10"><X size={24} strokeWidth={2.5} /></button>
          <div className="w-full max-w-5xl">
            <div className="relative pb-[56.25%] h-0 rounded-xl overflow-hidden shadow-2xl bg-black">
              {playingVideo.videoSrc ? (
                <video
                  className="absolute top-0 left-0 w-full h-full"
                  src={playingVideo.videoSrc}
                  poster={playingVideo.thumbnail}
                  controls
                  autoPlay
                  playsInline
                  data-testid="self-hosted-video"
                />
              ) : (
                <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${playingVideo.videoId}?autoplay=1`} title={playingVideo.caption} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              )}
            </div>
            <div className="mt-4 text-center">
              <span className="inline-block px-3 py-1 bg-red-600 text-[#152846] text-xs rounded-full mb-2">{playingVideo.category}</span>
              <p className="text-[#152846] text-lg">{playingVideo.caption}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Memories;
